<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { required } from "../../../../utils/validation";
import {
  Item,
  RecordStatus,
} from "../../../../types/models";
import {
  documentActions,
  downloadPDF,
  emailPDF,
  formatDate,
  isEmpty,
  printPDF,
  sharePDF,
} from "../../../../utils/CommonFunctions";
import { tokenManager } from "../../../../api/utils/tokenManager";
import ItemStockGrid from "../../../../components/ui/Templates/ItemStockGrid.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import GridInput from "../../../../components/ui/form/GridInput.vue";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import DateInput from "../../../../components/ui/form/DateInput.vue";
import DataTable from "../../../../components/table/DataTable.vue";
import IconButton from "../../../../components/ui/form/IconButton.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  recordStatus?: RecordStatus[];
  cartItems?: Item[];
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save"): void;
  (e: "viewDocument", data: any, title: string, routePath: string): void;
}>();

const isReadOnly = ref<boolean>(props.mode === "view");

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  setFormData,
  resetForm,
} = useFormValidation({
  id: props.data?.id || "",
  outlets_id: props.data?.outlets_id || "",
  warehouses_id: props.data?.warehouses_id || "",
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  ref_no: props.data?.ref_no || "",
  attachment: props.data?.attachment || "",
  reason: props.data?.reason || "",
  fresh_count: props.data?.fresh_count || false,
  adjustment_account_id: props.data?.adjustment_account_id || "",
  remarks: props.data?.remarks || "",
  uid: props.data?.id || "",
  items: [] as any,
  download: false,
});

//setFieldRules("outlets_id", [required("Select Outlet")]);
setFieldRules("warehouses_id", [required("Select Warehouse")]);
//setFieldRules("reason", [required("Enter reason")]);
setFieldRules("adjustment_account_id", [required("Select Adjustment Account")]);
//setFieldRules("items", [required("Select at least 1 item")]);

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setFormData(response.data);
  }
};


const handleSubmit = async (
  new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (validateForm()) {    
    formData.value.download = download || share;
    const action = formData.value.uid != '' ? 'update' : 'create'
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show("saved successfully");
      emit("save");
      if (new_record) {
        clearForm();
      } else if (download) {
        downloadPDF(response.data.file);
      } else if (mail) {
        emailPDF(props.endPoint, response.data.uid);
      } else if (share) {
        sharePDF(
          response.data.file, 'Stock Request #: ' + response.data.ref_no, "viber"
        );
      } else if (print) {
        printPDF(response.data.file);
      } else {
        setFormData(response.data);
      }
    }
  }
};

const clearForm = () => {
  resetForm();

  fields.value.date_time.value = formatDate(new Date());
  fields.value.warehouses_id.value = tokenManager.getSetting(
    "default_warehouse",
    true
  );
  isReadOnly.value = false;

  nextTick(() => {
    const input = document.getElementById("customers_id");
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

const allowNewItemRow = ref(false);

const prepareItemRow = () => {
  if (!isEmpty(formData.value.warehouses_id)) {
    if (props.cartItems) prepareCartItems();
    allowNewItemRow.value = true;
  } else {
    allowNewItemRow.value = false;
  }
};

const prepareCartItems = async () => {
  const ids = props.cartItems?.map((item) => item.id);
  const response = await api.post(
    "clients/inventory/item-management/items/items",
    {
      ids: ids,
      warehouses_id: formData.value.warehouses_id,
    }
  );
  if (response.success == true) {
    if (itemGridRef.value) {
      itemGridRef.value.handleSelectedItems(response.data);
    }
  }
};

const itemGridRef = ref<InstanceType<typeof ItemStockGrid> | null>(null);


const handleActions = async (action: string) => {
  documentActions(props.endPoint, formData.value.uid, action);
};

const showForm = ref(false);
onMounted(() => {
  showForm.value = true;
  isReadOnly.value = props.mode == "view";
  if (props.mode != "create") {
    getRecord();
  } else {
    clearForm();
  }
});
</script>

<template>
  <ModalFormComponent :width="mode == 'view' ? '100%' : '50rem'" @close="$emit('close')" :fullscreen="mode == 'view'">
    <template #header>
      <!-- Header -->
      <DocumentHeader
        :title="title"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-drop-down="isReadOnly"
        @close="$emit('close')"
        :permissions="permissions"
        @clear="clearForm"
        @new="clearForm"
        @save="handleSubmit(false)"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="false"
        :show-save-icon="true"
      >
        <template #dropdownbuttons> </template>
      </DocumentHeader>
    </template>
    <!-- Main Content - Scrollable -->
    <template #form>
      <!--header-->
      <div class="grid grid-cols-2 gap-6 mb-8" :class="{'md:grid-cols-5': mode == 'view'}">
        <SearchSelect
          label="Outlet"
          end-point="system/company/outlets"
          v-model="fields.outlets_id.value"
          :readonly="isReadOnly || mode != 'create'"
          :error="fields.outlets_id.touched ? fields.outlets_id.error : null"
          @blur="fields.outlets_id.touched = true"
        />
        <SearchSelect
          label="Warehouse"
          end-point="system/company/warehouses"
          v-model="fields.warehouses_id.value"
          :readonly="isReadOnly || mode != 'create'"
          @select="prepareItemRow"
          :error="
            fields.warehouses_id.touched ? fields.warehouses_id.error : null
          "
          @blur="fields.warehouses_id.touched = true"
        />
        <SearchSelect
          label="Adjustment Account"
          end-point="finance/accounting/chart-of-accounts"
          v-model="fields.adjustment_account_id.value"
          :readonly="isReadOnly"
          :error="
            fields.adjustment_account_id.touched ? fields.adjustment_account_id.error : null
          "
          @blur="fields.adjustment_account_id.touched = true"
        />
        <TextInput
            v-model="fields.remarks.value"
            label="Remarks"
            :readonly="isReadOnly"
            :error="fields.remarks.touched ? fields.remarks.error : null"
            @blur="fields.remarks.touched = true" />

        <div class="col-span-1 grid grid-cols-2 gap-6">
          <CheckInput
            v-model="fields.fresh_count.value"
            label="Fresh Count"
            :readonly="isReadOnly"
            :error="fields.fresh_count.touched ? fields.fresh_count.error : null"
            @blur="fields.fresh_count.touched = true" />
          <DateInput
            v-model="fields.date_time.value"
            label="Date"
            :readonly="isReadOnly"
            :error="fields.date_time.touched ? fields.date_time.error : null"
            @blur="fields.date_time.touched = true" />
          <TextInput
            v-model="fields.ref_no.value"
            v-if="mode != 'create'"
            label="Reference No"
            :readonly="true"
            :error="fields.ref_no.touched ? fields.ref_no.error : null"
            @blur="fields.ref_no.touched = true" />

        </div>
      </div>
        <GridInput label="Items" :error="fields.items.touched ? fields.items.error : null" v-if="mode == 'view'">
          <table
      class="divide-y divide-gray-200 dark:divide-gray-700" id="table-records">
      <thead>
        <tr id="table-headers">
          <th class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
          <th class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
          <th class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Unit of Measure</th>
          <th class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">On Hand</th>
          <th class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quantity</th>
          <th class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Packing Qty</th>
          <th class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Inventory Qty</th>
        </tr>
        </thead>
        <tbody
        class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
        <template  v-for="item in formData.items" :key="item.id">
        <tr class="hover:bg-gray dark:hover:bg-gray-700">
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
            <div :class="{'flex justify-start': item.batches || item.serials || item.shelfs, 'pl-[35px]' : (item.batches && item.batches.length >= 0) || (item.serials || item.shelfs.length >= 0) || (item.serials || item.shelfs.length >= 0)}">
                <IconButton
                  @click="item.expanded = false"
                  icon="CircleArrowDown"
                  class="mr-3"
                  v-if="item.expanded == true && (item.batches.length > 0 || item.serials.length > 0 || item.shelfs.length > 0)"
                />
                <IconButton
                  @click="item.expanded = true"
                  icon="CircleArrowRight"
                  class="mr-3"
                  v-if="item.expanded != true && (item.batches.length > 0 || item.serials.length > 0 || item.shelfs.length > 0)"
                />
            {{ item.code }}
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{{ item.name }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{{ item.unit_of_measure }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{{ item.on_hand }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{{ item.quantity }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{{ item.uom_qty }}</td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{{ item.inventory_qty }}</td>
        </tr>
        <tr v-if="item.batches && item.batches.length > 0 && item.expanded">
          <td colspan="7" class="pl-[50px]">
            <DataTable
          title="Batches"
          :rows="item.batches"
          :fields="[
            { name: 'batch_no', caption: 'Batch No', sortable: false, fieldType: 'text' },
            { name: 'quantity', caption: 'Quantity', sortable: false, fieldType: 'number' },
            { name: 'expiry_date', caption: 'Expiry Date', sortable: false, fieldType: 'date' },
            { name: 'status', caption: 'Status', sortable: false, fieldType: 'text' },
          ]"
          :readonly="true"
          :show-header="false"
          :show-actions="false"
          :show-footer="false"
          :show-pagination="false"
          :show-search="false"
          :show-filter="false"
          :show-select-all="false" />
          </td>
        </tr>
        <tr v-if="item.serials && item.serials.length > 0 && item.expanded">
          <td colspan="7" class="pl-[50px]">
            <DataTable
          title="Serials"
          :rows="item.serials"
          :fields="[
            { name: 'serial_no', caption: 'Serial No', sortable: false, fieldType: 'text' },
            { name: 'expiry_date', caption: 'Expiry Date', sortable: false, fieldType: 'date' },
            { name: 'status', caption: 'Status', sortable: false, fieldType: 'text' },
          ]"
          :readonly="true"
          :show-header="false"
          :show-actions="false"
          :show-footer="false"
          :show-pagination="false"
          :show-search="false"
          :show-filter="false"
          :show-select-all="false" />
          </td>
        </tr>
        <tr v-if="item.shelfs && item.shelfs.length > 0 && item.expanded">
          <td colspan="7" class="pl-[50px]">
            <DataTable
          title="Shelfs"
          :rows="item.shelfs"
          :fields="[
            { name: 'shelf_code', caption: 'Shelf Code', sortable: false, fieldType: 'text' },
            { name: 'shelf_name', caption: 'Shelf Name', sortable: false, fieldType: 'text' },
            { name: 'quantity', caption: 'Quantity', sortable: false, fieldType: 'number' },
            { name: 'status', caption: 'Status', sortable: false, fieldType: 'text' },
          ]"
          :readonly="true"
          :show-header="false"
          :show-actions="false"
          :show-footer="false"
          :show-pagination="false"
          :show-search="false"
          :show-filter="false"
          :show-select-all="false" />
          </td>
        </tr>
      </template>
        </tbody>
        </table>
        
      </GridInput>
      
    </template>
    <!-- Footer -->
    <template #footer>
      <DocumentFooter
        :data="formData"
        :readonly="isReadOnly"
        :end-point="endPoint"
        :permissions="permissions"
        :title="title"
        convert-module="Debit Note"
        @close="$emit('close')"
        @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @new="clearForm"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="false"
        :show-save-icon="true"
      />
    </template>
  </ModalFormComponent>
</template>
